"use client"

import { Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { MagnifyingGlass, XMark } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import { Locale } from "@lib/data/locales"
import type { MenuBrand } from "@lib/util/home-categories"
import type { NavSegment } from "@lib/util/nav-menu"
import { t } from "@lib/i18n"
import { normalizeLocale } from "@lib/i18n/locales"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import ProductSearch from "@modules/common/components/product-search"
import ContactMenu from "@modules/layout/components/contact-menu"
import LanguageSelect from "@modules/layout/components/language-select"
import MegaMenu from "@modules/layout/components/mega-menu"
import SideMenu from "@modules/layout/components/side-menu"

const CLOSE_DELAY_MS = 180

const navLinkClass =
  "relative z-[70] py-1 text-[15px] font-medium text-vx-ink/80 transition-colors hover:text-vx-ink"

type MainNavProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
  brands: MenuBrand[]
  segments: NavSegment[]
  year: number
  cartButton: ReactNode
}

const MainNav = ({
  regions,
  locales,
  currentLocale,
  brands,
  segments,
  year,
  cartButton,
}: MainNavProps) => {
  const locale = normalizeLocale(currentLocale)
  const [searchOpen, setSearchOpen] = useState(false)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const openMenu = useCallback(
    (menuId: string) => {
      clearCloseTimer()
      setOpenMenuId(menuId)
    },
    [clearCloseTimer]
  )

  const closeMenu = useCallback(() => {
    clearCloseTimer()
    setOpenMenuId(null)
  }, [clearCloseTimer])

  const scheduleCloseMenu = useCallback(() => {
    clearCloseTimer()
    closeTimer.current = setTimeout(() => {
      setOpenMenuId(null)
      closeTimer.current = null
    }, CLOSE_DELAY_MS)
  }, [clearCloseTimer])

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer])

  useEffect(() => {
    if (!searchOpen) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSearchOpen(false)
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [searchOpen])

  useEffect(() => {
    if (!openMenuId) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu()
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [openMenuId, closeMenu])

  const menuControl = (menuId: string) => ({
    menuId,
    open: openMenuId === menuId,
    onOpen: () => openMenu(menuId),
    onClose: closeMenu,
    onCancelClose: clearCloseTimer,
    onScheduleClose: scheduleCloseMenu,
  })

  return (
    <nav className="content-container flex flex-wrap items-center gap-x-4 gap-y-3 py-3.5 small:flex-nowrap small:py-4">
      <div className="flex min-w-0 items-center gap-3 small:gap-8">
        <div className="h-full shrink-0 small:hidden">
          <SideMenu
            regions={regions}
            locales={locales}
            currentLocale={currentLocale}
            brands={brands}
            segments={segments}
            year={year}
          />
        </div>
        <LocalizedClientLink
          href="/"
          className="relative z-[70] shrink-0 text-lg font-semibold tracking-wide text-vx-ink hover:text-vx-accent transition-colors"
          data-testid="nav-store-link"
        >
          VX Work
        </LocalizedClientLink>

        {!searchOpen && (
          <div className="z-[70] hidden items-center gap-x-7 small:flex">
            <LocalizedClientLink
              href="/solutions"
              className={navLinkClass}
              data-testid="nav-solutions-link"
              onMouseEnter={scheduleCloseMenu}
            >
              {t(locale, "nav.solutions")}
            </LocalizedClientLink>
            <LocalizedClientLink
              href="/guides"
              className={navLinkClass}
              data-testid="nav-guides-link"
              onMouseEnter={scheduleCloseMenu}
            >
              {t(locale, "nav.guides")}
            </LocalizedClientLink>
            <MegaMenu
              mode="shop"
              label={t(locale, "nav.shop")}
              brands={brands}
              segments={segments}
              {...menuControl("shop")}
            />
            <LocalizedClientLink
              href="/used"
              className={navLinkClass}
              data-testid="nav-used-link"
              onMouseEnter={scheduleCloseMenu}
            >
              {t(locale, "nav.used")}
            </LocalizedClientLink>
            <LocalizedClientLink
              href="/ecosystem"
              className={navLinkClass}
              data-testid="nav-ecosystem-link"
              onMouseEnter={scheduleCloseMenu}
            >
              {t(locale, "nav.ecosystem")}
            </LocalizedClientLink>
            <ContactMenu
              label={t(locale, "nav.contact")}
              {...menuControl("contact")}
            />
          </div>
        )}
      </div>

      {searchOpen ? (
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Suspense
            fallback={
              <div className="h-9 w-full animate-pulse bg-vx-elevated" />
            }
          >
            <ProductSearch
              variant="overlay"
              id="nav-overlay-search"
              autoFocus
              onEscape={() => setSearchOpen(false)}
            />
          </Suspense>
          <button
            type="button"
            onClick={() => setSearchOpen(false)}
            className="grid min-h-11 min-w-11 shrink-0 place-items-center text-vx-muted hover:text-vx-ink"
            aria-label={t(locale, "nav.searchClose")}
            data-testid="nav-search-close"
          >
            <XMark />
          </button>
        </div>
      ) : (
        <>
          <div className="order-3 w-full small:hidden">
            <Suspense
              fallback={
                <div className="h-10 w-full animate-pulse rounded-full bg-vx-elevated" />
              }
            >
              <ProductSearch variant="nav" id="nav-product-search-mobile" />
            </Suspense>
          </div>

          <div className="relative z-[70] ml-auto flex items-center gap-x-4 text-vx-muted small:gap-x-5">
            <button
              type="button"
              onClick={() => {
                closeMenu()
                setSearchOpen(true)
              }}
              onMouseEnter={scheduleCloseMenu}
              className="hidden min-h-11 min-w-11 place-items-center text-vx-ink/70 transition-colors hover:text-vx-ink small:grid"
              aria-label={t(locale, "nav.searchOpen")}
              data-testid="nav-search-open"
            >
              <MagnifyingGlass />
            </button>
            {!!locales?.length && (
              <div className="hidden small:block" onMouseEnter={scheduleCloseMenu}>
                <LanguageSelect
                  variant="header"
                  locales={locales}
                  currentLocale={currentLocale}
                />
              </div>
            )}
            <LocalizedClientLink
              className="hidden text-[14px] font-medium text-vx-ink/80 hover:text-vx-ink transition-colors small:inline"
              href="/account"
              data-testid="nav-account-link"
              onMouseEnter={scheduleCloseMenu}
            >
              {t(locale, "nav.account")}
            </LocalizedClientLink>
            {cartButton}
          </div>
        </>
      )}
    </nav>
  )
}

export default MainNav
